import { useState, useMemo, useCallback } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CircularProgress from '@mui/material/CircularProgress';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import type { MarketingMessage, TrialAnchor, VoiceAnchor } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { config } from '@/config';
import type { AppEnvironment } from '@/types/environment';
import { ControlledTabBar } from '../TabView';

// ─── Trial File ID Map ──────────────────────────────────────────────────────

const trialFileIds: Record<string, Record<AppEnvironment, string>> = {
  CEPHEUS: {
    development: '4653',
    sandbox: '4653',
    uat: '',
    production: '157',
  },
  MAIA: {
    development: '4652',
    sandbox: '4652',
    uat: '',
    production: '155',
  },
  PERSEUS: {
    development: '4651',
    sandbox: '4651',
    uat: '',
    production: '156',
  },
};

function getTrialFileLink(studyId: string, pageNo: number | null): string | null {
  const entry = trialFileIds[studyId.toUpperCase()];
  if (!entry) return null;
  const fileId = entry[config().APP_ENVIRONMENT];
  if (!fileId) return null;
  return `/workspaces/files/${fileId}${pageNo ? `?p=${pageNo}` : ''}`;
}

type EvidenceTab = 'trials' | 'transcripts';

interface SupportingEvidenceModalProps {
  open: boolean;
  onClose: () => void;
  message: MarketingMessage | null;
}

export function SupportingEvidenceModal({
  open,
  onClose,
  message,
}: SupportingEvidenceModalProps): React.JSX.Element | null {
  const [tab, setTab] = useState<EvidenceTab>('trials');

  if (!message) return null;

  const hasTrials = message.trialAnchors.length > 0;
  const hasVoice = message.voiceAnchors.length > 0;

  return (
    <Dialog fullWidth maxWidth="md" onClose={onClose} open={open} PaperProps={{ sx: { height: '90vh' } }}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          fontWeight: 700,
        }}
      >
        Supporting Evidence
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <MessageSummarySection>
        <MessageHeadline>{message.headline}</MessageHeadline>
        <MessageBody>{message.body}</MessageBody>
      </MessageSummarySection>
      <ControlledTabBar
        activeKey={tab}
        classes={{ tab: evidenceTabClass }}
        tabs={[
          {
            key: 'trials',
            title: `Clinical Trials (${message.trialAnchors.length})`,
            onClick: () => setTab('trials'),
          },
          {
            key: 'transcripts',
            title: `Qual Transcripts (${message.voiceAnchors.length})`,
            onClick: () => setTab('transcripts'),
          },
        ]}
      />
      <DialogContent sx={{ p: 0, flex: 1, overflow: 'auto' }}>
        {tab === 'trials' &&
          (hasTrials ? (
            <ClinicalTrialsPanel anchors={message.trialAnchors} />
          ) : (
            <EmptyState>No clinical trial evidence available.</EmptyState>
          ))}
        {tab === 'transcripts' &&
          (hasVoice ? (
            <VoiceAnchorsPanel anchors={message.voiceAnchors} />
          ) : (
            <EmptyState>No qual transcript evidence available.</EmptyState>
          ))}
      </DialogContent>
    </Dialog>
  );
}

// ─── Clinical Trials Panel ──────────────────────────────────────────────────

function ClinicalTrialsPanel({ anchors }: { anchors: TrialAnchor[] }): React.JSX.Element {
  const grouped = useMemo(() => {
    const map = new Map<string, Map<string, TrialAnchor[]>>();
    for (const a of anchors) {
      const raw = a.category || 'Uncategorized';
      const catKey = raw.replace(/\b\w/g, (c) => c.toUpperCase());
      if (!map.has(catKey)) map.set(catKey, new Map());
      const studyMap = map.get(catKey)!;
      const studyKey = a.studyId || 'Unknown Study';
      if (!studyMap.has(studyKey)) studyMap.set(studyKey, []);
      studyMap.get(studyKey)!.push(a);
    }
    return map;
  }, [anchors]);

  return (
    <PanelRoot>
      {[...grouped.entries()].map(([category, studyMap]) => (
        <CollapsibleSection key={category} title={category} defaultOpen>
          {[...studyMap.entries()].map(([study, items]) => (
            <CollapsibleSection key={study} title={`${study} (${items.length})`} nested>
              {items.map((item) => {
                const fileLink = getTrialFileLink(
                  item.studyId,
                  item.sourceSlides ? item.sourceSlides[0] : null
                );
                return (
                  <ClaimCard key={item.claimId}>
                    <ClaimText>{item.claimText}</ClaimText>
                    {/* <ClaimMeta>
                      {item.studyPhase && <MetaTag>{item.studyPhase}</MetaTag>}
                      {item.primaryEndpoint && <MetaTag>{item.primaryEndpoint}</MetaTag>}
                      {item.targetPatientType && <MetaTag>{item.targetPatientType}</MetaTag>}
                    </ClaimMeta> */}
                    {fileLink && (
                      <ViewSourceLink href={fileLink} target="_blank" rel="noopener noreferrer">
                        View Source <OpenInNewIcon sx={{ fontSize: 12, ml: 0.5 }} />
                      </ViewSourceLink>
                    )}
                  </ClaimCard>
                );
              })}
            </CollapsibleSection>
          ))}
        </CollapsibleSection>
      ))}
    </PanelRoot>
  );
}

// ─── Voice Anchors Panel ────────────────────────────────────────────────────

function VoiceAnchorsPanel({ anchors }: { anchors: VoiceAnchor[] }): React.JSX.Element {
  const grouped = useMemo(() => {
    const map = new Map<string, Map<string, VoiceAnchor[]>>();
    for (const a of anchors) {
      const theme = a.themeLabel || 'Other';
      const respondent = a.respondentSegment ? `${a.respondentId} (${a.respondentSegment})` : a.respondentId;
      if (!map.has(theme)) map.set(theme, new Map());
      const respondentMap = map.get(theme)!;
      if (!respondentMap.has(respondent)) respondentMap.set(respondent, []);
      respondentMap.get(respondent)!.push(a);
    }
    return map;
  }, [anchors]);

  return (
    <PanelRoot>
      {[...grouped.entries()].map(([theme, respondentMap]) => (
        <CollapsibleSection key={theme} title={theme} defaultOpen>
          {[...respondentMap.entries()].map(([respondent, items]) => (
            <CollapsibleSection key={respondent} title={`${respondent} (${items.length})`} nested>
              {items.map((item) => (
                <VoiceAnchorCard key={item.observationId} anchor={item} />
              ))}
            </CollapsibleSection>
          ))}
        </CollapsibleSection>
      ))}
    </PanelRoot>
  );
}

function VoiceAnchorCard({ anchor }: { anchor: VoiceAnchor }): React.JSX.Element {
  const [showQuote, setShowQuote] = useState(false);
  const [transcriptLink, setTranscriptLink] = useState<string | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const apiClient = useChatApiClient();

  const handleViewTranscript = useCallback(async () => {
    if (transcriptLink) {
      window.open(transcriptLink, '_blank');
      return;
    }
    setLinkLoading(true);
    try {
      const { link } = await apiClient.messageGeneration.getTranscriptLink(
        anchor.respondentId,
        anchor.quoteText,
        (anchor.turnIndices[0] || 0) - 1
      );
      setTranscriptLink(link);
      window.open(link, '_blank');
    } catch {
      // silently fail — button stays available for retry
    } finally {
      setLinkLoading(false);
    }
  }, [apiClient, anchor.respondentId, anchor.quoteText, transcriptLink]);

  return (
    <VoiceCard>
      <VoiceSummary>{anchor.summary}</VoiceSummary>
      <VoiceActions>
        <ShowQuoteButton onClick={() => setShowQuote((v) => !v)}>
          {showQuote ? 'Hide Quote' : 'Show Quote'}
        </ShowQuoteButton>
      </VoiceActions>
      {showQuote && (
        <QuoteBlock>
          <QuoteText>&ldquo;{anchor.quoteText}&rdquo;</QuoteText>
          <QuoteFooter>
            <TranscriptLink onClick={handleViewTranscript}>
              {linkLoading ? (
                <CircularProgress size={10} sx={{ mr: 0.5 }} />
              ) : (
                <OpenInNewIcon sx={{ fontSize: 10, mr: 0.5 }} />
              )}
              View Transcript
            </TranscriptLink>
          </QuoteFooter>
        </QuoteBlock>
      )}
    </VoiceCard>
  );
}

// ─── Collapsible Section ────────────────────────────────────────────────────

function CollapsibleSection({
  title,
  children,
  defaultOpen = false,
  nested = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  nested?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <SectionRoot $nested={nested}>
      <SectionHeader onClick={() => setOpen((v) => !v)} $nested={nested}>
        <SectionTitle $nested={nested}>{title}</SectionTitle>
        <ExpandIcon $open={open}>
          <ExpandMoreIcon fontSize="small" />
        </ExpandIcon>
      </SectionHeader>
      {open && <SectionContent>{children}</SectionContent>}
    </SectionRoot>
  );
}

// ─── Styled Components ──────────────────────────────────────────────────────

const evidenceTabClass = css`
  width: 200px !important;
`;

const MessageSummarySection = styled.div`
  padding: 8px 24px 12px;
  margin-top: 8px;
  border-bottom: 1px solid #c8ddf0;
  background: #f8fbfe;
`;

const MessageHeadline = styled.h3`
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 600;
  color: #1d2e55;
`;

const MessageBody = styled.p`
  margin: 0;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
  max-height: 120px;
  overflow-y: auto;
`;

const PanelRoot = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #999;
  font-size: 14px;
`;

const SectionRoot = styled.div<{ $nested?: boolean }>`
  border: 1px solid ${({ $nested }) => ($nested ? '#e0eaf3' : '#c8ddf0')};
  border-radius: ${({ $nested }) => ($nested ? '6px' : '8px')};
  overflow: hidden;
  ${({ $nested }) => $nested && 'margin-left: 8px;'}
`;

const SectionHeader = styled.div<{ $nested?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ $nested }) => ($nested ? '8px 12px' : '10px 14px')};
  background: ${({ $nested }) => ($nested ? '#f0f7fc' : '#e8f2fa')};
  cursor: pointer;
  user-select: none;

  &:hover {
    background: ${({ $nested }) => ($nested ? '#e4eff8' : '#dceaf5')};
  }
`;

const SectionTitle = styled.span<{ $nested?: boolean }>`
  font-size: ${({ $nested }) => ($nested ? '12px' : '13px')};
  font-weight: 600;
  color: ${({ $nested }) => ($nested ? '#1d2e55' : '#1D94C7')};
`;

const ExpandIcon = styled.span<{ $open: boolean }>`
  display: flex;
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  color: #28aae1;
`;

const SectionContent = styled.div`
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ClaimCard = styled.div`
  padding: 10px 12px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background: white;
`;

const ClaimText = styled.div`
  font-size: 13px;
  color: #333;
  line-height: 1.5;
`;

const ClaimMeta = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const MetaTag = styled.span`
  padding: 2px 8px;
  background: #e8f4fb;
  color: #1d94c7;
  border: 1px solid #c8e3f4;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 500;
`;

const ViewSourceLink = styled.a`
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  font-size: 12px;
  font-weight: 500;
  color: #1d94c7;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    color: var(--hyperlink);
  }
`;

const VoiceCard = styled.div`
  padding: 10px 12px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const VoiceSummary = styled.div`
  font-size: 13px;
  color: #333;
  line-height: 1.5;
`;

const VoiceActions = styled.div`
  display: flex;
`;

const ShowQuoteButton = styled.button`
  padding: 2px 10px;
  border: 1px solid #c8e3f4;
  border-radius: 4px;
  background: #f0f8fd;
  color: #1d94c7;
  font-size: 11px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.15s ease;

  &:hover {
    background: #e0f0fa;
    color: #1d94c7;
    border-color: #28aae1;
  }
`;

const QuoteBlock = styled.blockquote`
  margin: 0;
  padding: 10px 14px;
  border-left: 3px solid #28aae1;
  background: #f4fafd;
  border-radius: 0 6px 6px 0;
`;

const QuoteText = styled.div`
  font-size: 13px;
  color: #444;
  font-style: italic;
  line-height: 1.6;
`;

const QuoteFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
`;

const TranscriptLink = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: var(--hyperlink);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
